/**
 * Validates a password against common security requirements.
 *
 * Password must contain:
 * - At least one uppercase letter (A-Z)
 * - At least one lowercase letter (a-z)
 * - At least one numeric character (0-9)
 * - At least one character that is neither a letter nor a digit (the server's rule)
 * - Minimum length (default: 8 characters)
 *
 * @param password - The password string to validate
 * @param minLength - Minimum required length (default: 8)
 * @returns `true` if valid, or the message of the failed local copy - one for the length, one for
 * every strength rule together, as Identity answers
 */
/**
 * The texts of the local copies of Identity's password policy - the one source for every screen
 * that renders them (the change-password form's catalogue, the reset and confirmation pages).
 * Identity answers with the same two identities: too short, and too weak for every strength rule
 * together.
 */
export const passwordMessages = {
  tooShort: (minLength: number): string => `Min. ${minLength} characters`,
  tooWeak:
    'Use upper and lower case letters, a digit and a character that is neither a letter nor a digit',
  /** What the reset and confirmation pages say above the field. */
  hint: 'Password needs to be at least 8 characters long and contain a lowercase and an uppercase letter, a digit and a character that is neither a letter nor a digit.',
};

export const validatePassword = (password: string, minLength = 8): true | string => {
  const upperCaseLetters = /[A-Z]/;
  const lowerCaseLetters = /[a-z]/;
  const numericCharacters = /[0-9]/;
  const specialCharacters = /[^A-Za-z0-9]/;

  if (password.length < minLength) return passwordMessages.tooShort(minLength);
  if (
    !upperCaseLetters.test(password) ||
    !lowerCaseLetters.test(password) ||
    !numericCharacters.test(password) ||
    !specialCharacters.test(password)
  ) {
    return passwordMessages.tooWeak;
  }
  return true;
};
